// Shared bulk-add schemas + payload builders (used by the Bulk Add page).
// =====================================================================
// Helpers
// =====================================================================
const numOr = (v) =>
  v !== "" && v != null ? Number(String(v).replace(/,/g, "")) : undefined;
const todayISO = () => new Date().toISOString().slice(0, 10);
const PER_UNIT = ["gold", "silver"];
const isPerUnit = (c) => PER_UNIT.includes(c);

const deriveTenure = (start, end, tenureYears) => {
  const s = start || todayISO();
  let tenure = numOr(tenureYears);
  if (!tenure && end) {
    const yrs = (new Date(end) - new Date(s)) / (365.25 * 24 * 3600 * 1000);
    if (yrs > 0) tenure = Math.max(1, Math.round(yrs));
  }
  return { start: s, tenure };
};

// Column kinds: text | money | number | percent | date | select | switch
// A column is "active" for a row based on the row's selected type.

// ── ASSETS ──────────────────────────────────────────────────────────
const ASSET_CFG = {
  mutual_funds: { sip: "Monthly SIP (₹)", ret: "Expected Return %" },
  stocks: { ret: "Expected Return %", qty: "Number of Shares" },
  bank: { ret: "Interest Rate %", hidePurchase: true, liquid: true },
  fd: { tenure: true, ret: "Interest Rate %", purchase: "Principal Invested (₹)" },
  rd: { tenure: true, sip: "Monthly Deposit (₹)", ret: "Interest Rate %", purchase: "Invested So Far (₹)" },
  mis: { tenure: true, ret: "Interest Rate % p.a.", purchase: "Amount Invested (₹)" },
  bonds: { tenure: true, ret: "Coupon / YTM %" },
  gold: { ret: "Expected Return %", qty: "Gold (grams)" },
  silver: { ret: "Expected Return %", qty: "Silver (grams)" },
  crypto: { ret: "Expected Return %", qty: "Crypto Quantity" },
  real_estate: { ret: "Appreciation % p.a." },
  vehicle: { ret: "Depreciation % p.a." },
  business: { ret: "Expected Return %" },
  epf: { sip: "Monthly Contribution (₹)", ret: "Interest Rate %" },
  nps: { sip: "Monthly Contribution (₹)", ret: "Expected Return %" },
  cash: { ret: "Expected Return %", hidePurchase: true, liquid: true },
  collectibles: { ret: "Expected Return %", qty: "Quantity" },
  digital: { ret: "Expected Return %" },
  other: { ret: "Expected Return %" },
};
const acfg = (t) => ASSET_CFG[t] || ASSET_CFG.other;

const LIAB_CFG = {
  home_loan: { emi: true, tenure: true, rate: "Interest Rate % p.a.", orig: "Loan Amount (₹)", defRate: 8.5 },
  car_loan: { emi: true, tenure: true, rate: "Interest Rate % p.a.", orig: "Loan Amount (₹)", defRate: 9.5 },
  personal_loan: { emi: true, tenure: true, rate: "Interest Rate % p.a.", orig: "Loan Amount (₹)", defRate: 14 },
  credit_card: { revolving: true, rate: "APR / Interest % p.a.", orig: "Credit Limit (₹)", bal: "Current Balance (₹)", defRate: 42 },
  education_loan: { emi: true, tenure: true, rate: "Interest Rate % p.a.", orig: "Loan Amount (₹)", defRate: 9 },
  mortgage: { emi: true, tenure: true, rate: "Interest Rate % p.a.", orig: "Loan Amount (₹)", defRate: 9 },
  business_debt: { emi: true, tenure: true, rate: "Interest Rate % p.a.", orig: "Sanctioned Amount (₹)", defRate: 12 },
  informal: { rate: "Interest % p.a. (if any)", orig: "Borrowed Amount (₹)" },
  other: { rate: "Interest % p.a." },
};
const lcfg = (t) => LIAB_CFG[t] || LIAB_CFG.other;

const INS_CASH = new Set(["endowment", "ulip", "money_back", "whole_life"]);

const GOAL_INFLATION = {
  retirement: 6, education: 10, real_estate: 7, wedding: 7, vehicle: 5,
  travel: 5, business: 6, healthcare: 12, emergency: 6, other: 6,
};

// =====================================================================
// Entity schemas
// =====================================================================
const SCHEMAS = {
  assets: {
    title: "Bulk add assets",
    endpoint: "/assets",
    usageKey: "assets",
    typeKey: "category",
    typeLabel: "Type",
    typeOptions: Object.keys(ASSET_CFG).map((v) => ({
      value: v,
      label: v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    })),
    defaultType: "mutual_funds",
    defaultsFor: (t) => ({ is_liquid: !!acfg(t).liquid, expected_return: "" }),
    columns: [
      { key: "name", label: "Name", kind: "text", required: true, active: () => true, w: 160 },
      { key: "value", kind: "money", required: true, active: (t) => t !== "mis", w: 130,
        label: (t) => (isPerUnit(t) ? "Value / unit" : "Current Value") },
      { key: "quantity", kind: "number", w: 110, active: (t) => !!acfg(t).qty, label: (t) => acfg(t).qty || "Quantity" },
      { key: "purchase_value", kind: "money", w: 130, active: (t) => !acfg(t).hidePurchase,
        label: (t) => acfg(t).purchase || (isPerUnit(t) ? "Purchase / unit" : "Purchase Value") },
      { key: "purchase_date", kind: "date", w: 140, active: (t) => !acfg(t).hidePurchase, label: () => "Purchase Date" },
      { key: "expected_return", kind: "percent", w: 120, active: () => true, label: (t) => acfg(t).ret || "Return %" },
      { key: "monthly_contribution", kind: "money", w: 130, active: (t) => !!acfg(t).sip, label: (t) => acfg(t).sip || "Monthly SIP" },
      { key: "contribution_step_up", kind: "percent", w: 110, active: (t) => !!acfg(t).sip, label: () => "Step-Up %" },
      { key: "start_date", kind: "date", w: 140, active: (t) => !!acfg(t).tenure, label: () => "Start Date" },
      { key: "maturity_date", kind: "date", w: 140, active: (t) => !!acfg(t).tenure, label: () => "Maturity Date" },
      { key: "tenure_years", kind: "number", w: 100, active: (t) => !!acfg(t).tenure, label: () => "Tenure (yrs)" },
      { key: "institution", kind: "text", w: 140, active: () => true, label: () => "Institution" },
      { key: "is_liquid", kind: "switch", w: 80, active: () => true, label: () => "Liquid" },
    ],
    validate: (r) => {
      if (!r.name?.trim()) return "Name required";
      const v = r.category === "mis" ? numOr(r.purchase_value) : numOr(r.value);
      return !(v > 0)
        ? r.category === "mis"
          ? "Amount invested required"
          : "Value required"
        : null;
    },
    build: (r) => {
      const c = acfg(r.category);
      // MIS: the amount invested is the current value (principal is stable).
      const value = r.category === "mis" ? numOr(r.purchase_value) : numOr(r.value);
      const p = {
        name: r.name.trim(), category: r.category,
        value, institution: r.institution || undefined,
        expected_return: numOr(r.expected_return), is_liquid: !!r.is_liquid,
      };
      if (!c.hidePurchase) {
        p.purchase_value = numOr(r.purchase_value);
        p.purchase_date = r.purchase_date || undefined;
      }
      if (c.qty && r.quantity) p.quantity = Number(r.quantity);
      if (c.sip) {
        p.monthly_contribution = numOr(r.monthly_contribution) || 0;
        p.contribution_step_up = numOr(r.contribution_step_up) || 0;
      }
      p.has_tenure = !!c.tenure;
      if (c.tenure) {
        const { start, tenure } = deriveTenure(r.start_date, r.maturity_date, r.tenure_years);
        p.start_date = start;
        p.maturity_date = r.maturity_date || undefined;
        p.tenure_years = tenure;
      }
      return p;
    },
  },

  liabilities: {
    title: "Bulk add liabilities",
    endpoint: "/liabilities",
    usageKey: "liabilities",
    typeKey: "category",
    typeLabel: "Type",
    typeOptions: Object.keys(LIAB_CFG).map((v) => ({
      value: v, label: v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    })),
    defaultType: "home_loan",
    defaultsFor: (t) => ({ interest_rate: lcfg(t).defRate != null ? String(lcfg(t).defRate) : "", is_active: true }),
    columns: [
      { key: "name", label: () => "Name", kind: "text", required: true, active: () => true, w: 160 },
      { key: "original_amount", kind: "money", w: 140, active: () => true, label: (t) => lcfg(t).orig || "Original (₹)" },
      { key: "outstanding", kind: "money", required: true, w: 140, active: () => true, label: (t) => lcfg(t).bal || "Outstanding (₹)" },
      { key: "lender", kind: "text", w: 140, active: () => true, label: () => "Lender" },
      { key: "interest_rate", kind: "percent", w: 130, active: () => true, label: (t) => lcfg(t).rate || "Interest %" },
      { key: "emi", kind: "money", w: 120, active: (t) => !lcfg(t).revolving, label: () => "EMI / mo" },
      { key: "emi_date", kind: "number", w: 90, active: (t) => !lcfg(t).revolving, label: () => "EMI day" },
      { key: "start_date", kind: "date", w: 140, active: (t) => !lcfg(t).revolving, label: () => "Start Date" },
      { key: "end_date", kind: "date", w: 140, active: (t) => !lcfg(t).revolving, label: () => "End Date" },
      { key: "tenure_years", kind: "number", w: 100, active: (t) => !lcfg(t).revolving, label: () => "Tenure (yrs)" },
    ],
    validate: (r) => (!r.name?.trim() ? "Name required" : !(numOr(r.outstanding) > 0) ? "Outstanding required" : null),
    build: (r) => {
      const c = lcfg(r.category);
      const showEmi = !c.revolving;
      const showTenure = !c.revolving;
      const p = {
        name: r.name.trim(), category: r.category,
        outstanding: numOr(r.outstanding), original_amount: numOr(r.original_amount),
        lender: r.lender || undefined, is_active: true,
        interest_rate: numOr(r.interest_rate),
        is_emi: showEmi, emi: showEmi ? numOr(r.emi) : undefined,
        emi_date: showEmi && r.emi_date !== "" ? Number(r.emi_date) : undefined,
        has_tenure: showTenure,
      };
      if (showTenure) {
        const { start, tenure } = deriveTenure(r.start_date, r.end_date, r.tenure_years);
        p.start_date = start;
        p.end_date = r.end_date || undefined;
        p.tenure_years = tenure;
      }
      Object.keys(p).forEach((k) => p[k] === undefined && delete p[k]);
      return p;
    },
  },

  insurance: {
    title: "Bulk add insurance",
    endpoint: "/insurance",
    usageKey: "insurance",
    typeKey: "policy_type",
    typeLabel: "Policy Type",
    typeOptions: [
      { value: "term_life", label: "Term Life" }, { value: "endowment", label: "Endowment" },
      { value: "ulip", label: "ULIP" }, { value: "money_back", label: "Money Back" },
      { value: "whole_life", label: "Whole Life" }, { value: "health", label: "Health" },
      { value: "critical_illness", label: "Critical Illness" }, { value: "personal_accident", label: "Personal Accident" },
      { value: "motor", label: "Motor / Vehicle" }, { value: "other", label: "Other" },
    ],
    defaultType: "term_life",
    defaultsFor: () => ({ premium_frequency: "yearly", is_active: true }),
    columns: [
      { key: "name", label: () => "Policy Name", kind: "text", required: true, active: () => true, w: 170 },
      { key: "insurer", kind: "text", w: 140, active: () => true, label: () => "Insurer" },
      { key: "sum_assured", kind: "money", w: 140, active: () => true, label: () => "Sum Assured" },
      { key: "premium_amount", kind: "money", w: 120, active: () => true, label: () => "Premium" },
      { key: "premium_frequency", kind: "select", w: 130, active: () => true, label: () => "Frequency",
        options: [{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "half_yearly", label: "Half-Yearly" }, { value: "yearly", label: "Yearly" }, { value: "single", label: "Single" }] },
      { key: "policy_number", kind: "text", w: 140, active: () => true, label: () => "Policy No." },
      { key: "start_date", kind: "date", w: 140, active: () => true, label: () => "Start Date" },
      { key: "maturity_date", kind: "date", w: 140, active: () => true, label: () => "Maturity Date" },
      { key: "premium_paying_years", kind: "number", w: 110, active: () => true, label: () => "Paying Yrs" },
      { key: "current_value", kind: "money", w: 130, active: (t) => INS_CASH.has(t), label: () => "Cash Value" },
      { key: "maturity_value", kind: "money", w: 130, active: (t) => INS_CASH.has(t), label: () => "Maturity Value" },
      { key: "expected_return", kind: "percent", w: 110, active: (t) => INS_CASH.has(t), label: () => "Return %" },
      { key: "nominee", kind: "text", w: 130, active: () => true, label: () => "Nominee" },
    ],
    validate: (r) => (!r.name?.trim() ? "Name required" : null),
    build: (r) => {
      const cash = INS_CASH.has(r.policy_type);
      const p = {
        name: r.name.trim(), policy_type: r.policy_type,
        insurer: r.insurer || undefined, policy_number: r.policy_number || undefined,
        sum_assured: numOr(r.sum_assured), premium_amount: numOr(r.premium_amount) || 0,
        premium_frequency: r.premium_frequency || "yearly",
        start_date: r.start_date || undefined, maturity_date: r.maturity_date || undefined,
        premium_paying_years: r.premium_paying_years ? Number(r.premium_paying_years) : undefined,
        has_cash_value: cash, nominee: r.nominee || undefined, is_active: true,
      };
      if (cash) {
        p.current_value = numOr(r.current_value);
        p.maturity_value = numOr(r.maturity_value);
        p.expected_return = numOr(r.expected_return);
      }
      return p;
    },
  },

  goals: {
    title: "Bulk add goals",
    endpoint: "/goals",
    usageKey: "goals",
    typeKey: "category",
    typeLabel: "Category",
    typeOptions: [
      { value: "retirement", label: "Retirement" }, { value: "education", label: "Education" },
      { value: "real_estate", label: "Home / Property" }, { value: "healthcare", label: "Healthcare" },
      { value: "travel", label: "Travel" }, { value: "vehicle", label: "Vehicle" },
      { value: "wedding", label: "Wedding" }, { value: "business", label: "Business" },
      { value: "emergency", label: "Emergency Fund" }, { value: "other", label: "Other" },
    ],
    defaultType: "retirement",
    defaultsFor: () => ({ tracking_frequency: "yearly" }),
    columns: [
      { key: "name", label: () => "Goal Name", kind: "text", required: true, active: () => true, w: 180 },
      { key: "target_amount", kind: "money", required: true, w: 150, active: () => true, label: () => "Target Amount" },
      { key: "current_amount", kind: "money", w: 140, active: () => true, label: () => "Already Saved" },
      { key: "target_date", kind: "date", required: true, w: 150, active: () => true, label: () => "Target Date" },
      { key: "inflation_rate", kind: "percent", w: 120, active: () => true, label: () => "Inflation %",
        placeholder: (t) => `${GOAL_INFLATION[t] ?? 6}` },
      { key: "tracking_frequency", kind: "select", w: 130, active: () => true, label: () => "Tracking",
        options: [{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "yearly", label: "Yearly" }] },
    ],
    validate: (r) =>
      !r.name?.trim() ? "Name required" : !(numOr(r.target_amount) > 0) ? "Target required" : !r.target_date ? "Date required" : null,
    build: (r) => {
      const p = {
        name: r.name.trim(), category: r.category,
        target_amount: numOr(r.target_amount), current_amount: numOr(r.current_amount) || 0,
        target_date: r.target_date, tracking_frequency: r.tracking_frequency || "yearly",
        is_retirement: r.category === "retirement",
      };
      if (r.inflation_rate !== "" && r.inflation_rate != null) p.inflation_rate = Number(r.inflation_rate);
      return p;
    },
  },
};

export { SCHEMAS, numOr, todayISO, isPerUnit };
